/** Generic debounced typeahead. Searches only while its list is open.
 *
 *  Tenant-owned records must show tenant + pk (console rule): pass
 *  `getOptionMeta` and the option, input text and chips render the record
 *  identity (see ./recordOption).
 *
 *  `errorText` (opt-in): a search that fails (not an abort) shows it in the open list,
 *  with an icon and **Retry**, instead of reading as "No options". Without it a failed
 *  search shows the empty list as it always did. */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Autocomplete, Box, Button, Chip, CircularProgress, TextField } from '@mui/material';
import type { HTMLAttributes, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { RecordOption, recordLabel, type RecordMeta } from './recordOption';

interface Props<T> {
  queryFn: (query: string, signal: AbortSignal) => Promise<T[]>;
  getOptionLabel: (opt: T) => string;
  getOptionKey: (opt: T) => string | number;
  value: T[] | T | null;
  onChange: (v: T[] | T | null) => void;
  label?: string;
  multiple?: boolean;
  placeholder?: string;
  debounceMs?: number;
  /** Also query with an empty string (browsable dropdown: the first page of
   *  results shows before the user types). Default: type-to-search only. */
  fetchOnEmpty?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  /** Custom dropdown-option renderer (the input still shows `getOptionLabel`).
   *  Spread `props` onto the root `<li>`. */
  renderOption?: (props: HTMLAttributes<HTMLLIElement>, option: T) => ReactNode;
  /** Record identity (name + pk + tenant). When set, options render two-line
   *  and the input text / chips use `Name · #pk · TENANT` — `getOptionLabel`
   *  is then only a fallback. */
  getOptionMeta?: (opt: T) => RecordMeta;
  /** Disable individual options (still shown). */
  getOptionDisabled?: (opt: T) => boolean;
  helperText?: ReactNode;
  error?: boolean;
  required?: boolean;
  noOptionsText?: ReactNode;
  /** The input's id — for a label rendered outside the field (`<label htmlFor>`). */
  id?: string;
  /** Group the dropdown options under headers (e.g. a tag's category). */
  groupBy?: (opt: T) => string;
  /** Multiple mode: render the picked values yourself (return null to hide them). */
  renderTags?: (selected: T[]) => ReactNode;
  /** Props for the underlying `<input>` (aria-describedby, aria-labelledby). */
  inputProps?: Record<string, unknown>;
  /** Opt-in: what a failed search says in the open list ("Couldn’t load the operator’s
   *  tours"), with Retry. Unset: a failure shows the empty list (the default). */
  errorText?: string;
  /** Opt-in: the input takes focus when mounted (a picker mounted on demand). */
  autoFocus?: boolean;
  /** Opt-in: focusing the input opens the list (MUI `openOnFocus`). */
  openOnFocus?: boolean;
  /** The input lost focus (a picker mounted on demand goes back to its value). */
  onBlur?: () => void;
}

export function RemoteAutocomplete<T>({
  queryFn,
  getOptionLabel,
  getOptionKey,
  value,
  onChange,
  label,
  multiple,
  placeholder,
  debounceMs = 200,
  fetchOnEmpty = false,
  size = 'medium',
  disabled = false,
  renderOption,
  getOptionMeta,
  getOptionDisabled,
  helperText,
  error,
  required,
  noOptionsText,
  id,
  groupBy,
  renderTags,
  inputProps,
  errorText,
  autoFocus,
  openOnFocus,
  onBlur,
}: Props<T>) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  // Only with `errorText`: the last search failed; `attempt` re-runs it (Retry).
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  // Callers pass inline closures. Reading the latest one through a ref keeps a parent
  // re-render from re-arming the search — it used to restart every picker's debounce on
  // every keystroke anywhere on the page.
  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;
  // The search that follows opening the list runs at once; typing is debounced.
  const justOpened = useRef(false);

  const eqKey = useMemo(() => getOptionKey, [getOptionKey]);
  const labelOf = useMemo(
    () => (getOptionMeta ? (o: T) => recordLabel(getOptionMeta(o)) : getOptionLabel),
    [getOptionMeta, getOptionLabel],
  );

  // As in MUI's own filtering: while the input still shows the picked value's label,
  // nothing has been searched for.
  const selected = !multiple && value != null && !Array.isArray(value) ? (value as T) : null;
  const term = selected !== null && input === labelOf(selected) ? '' : input.trim();

  // Debounced search, only while the list is open. A closed list shows nothing, and a
  // closed picker searching on mount (with its own label as the query) cost one request
  // per picker — 52 on a busy itinerary day.
  useEffect(() => {
    if (!open) return undefined;
    if (!term && !fetchOnEmpty) {
      // Type-to-search: offer the picked value until the user types.
      setOptions(selected !== null ? [selected] : []);
      return undefined;
    }
    const delay = justOpened.current ? 0 : debounceMs;
    justOpened.current = false;
    const t = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      if (errorText !== undefined) setFailed(false);
      try {
        const opts = await queryFnRef.current(term, ctrl.signal);
        if (!ctrl.signal.aborted) setOptions(opts);
      } catch {
        // Without `errorText`: ignored — aborts and network failures surface via React
        // Query elsewhere. With it: a failure (not an abort) is said in the list.
        if (errorText !== undefined && !ctrl.signal.aborted) {
          setOptions([]);
          setFailed(true);
        }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, delay);
    return () => window.clearTimeout(t);
    // `selected` is read only for the type-to-search fallback above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, term, debounceMs, fetchOnEmpty, attempt]);
  const failure =
    failed && errorText !== undefined ? (
      <Box role="alert" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }} data-autocomplete-error>
        <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
        <Box component="span" sx={{ flex: 1 }}>
          {errorText}
        </Box>
        <Button
          size="small"
          color="inherit"
          // Keep the focus (and the list) where it is.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            justOpened.current = true;
            setAttempt((a) => a + 1);
          }}
          sx={{ minWidth: 0, py: 0 }}
        >
          Retry
        </Button>
      </Box>
    ) : null;
  const optionRenderer = renderOption
    ? renderOption
    : getOptionMeta
      ? (props: HTMLAttributes<HTMLLIElement>, option: T) => (
          <RecordOption key={String(getOptionKey(option))} props={props} meta={getOptionMeta(option)} />
        )
      : undefined;

  return (
    <Autocomplete<T, boolean>
      {...(id ? { id } : {})}
      {...(groupBy ? { groupBy } : {})}
      size={size}
      disabled={disabled}
      multiple={Boolean(multiple)}
      value={value as never}
      onChange={(_, v) => onChange(v as T[] | T | null)}
      onInputChange={(_, v) => setInput(v)}
      open={open}
      onOpen={() => {
        justOpened.current = true;
        setOpen(true);
      }}
      onClose={() => setOpen(false)}
      {...(openOnFocus ? { openOnFocus: true } : {})}
      {...(onBlur ? { onBlur } : {})}
      options={options}
      getOptionLabel={labelOf}
      {...(getOptionDisabled ? { getOptionDisabled } : {})}
      {...(failure ? { noOptionsText: failure } : noOptionsText !== undefined ? { noOptionsText } : {})}
      // Server-side search is authoritative: MUI's default label filtering would
      // hide results matched on fields absent from the label (e.g. email).
      filterOptions={(opts) => opts}
      isOptionEqualToValue={(a, b) => eqKey(a) === eqKey(b)}
      loading={loading}
      {...(optionRenderer ? { renderOption: optionRenderer } : {})}
      {...(renderTags && multiple
        ? { renderTags: (selected: T[]) => renderTags(selected) }
        : getOptionMeta && multiple
        ? {
            renderTags: (selected: T[], getTagProps: (a: { index: number }) => Record<string, unknown>) =>
              selected.map((opt, index) => {
                const { key, ...tagProps } = getTagProps({ index }) as { key: string } & Record<string, unknown>;
                return <Chip key={key} size="small" label={labelOf(opt)} {...tagProps} />;
              }),
          }
        : {})}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          {...(autoFocus ? { autoFocus: true } : {})}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          required={required}
          inputProps={{ ...params.inputProps, ...inputProps }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
